import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Helmet } from 'react-helmet';
import { List, ListItem } from 'chaoskit/src/components';

import BaseLayout from '../../layouts/BaseLayout';
import Hero from '../../partials/Hero';
import { Data } from '../../components';
import CaseStudyQuote from '../../partials/customers/CaseStudyQuote';

const CaseStudyQuoteSection = ({ text, name, title }) => (
  <div className="caseStudy__section-quote u-contrast">
    <Data markdown>{text}</Data>

    {name && (
      <h4 className="u-textFluid--xlarge u-mt--large u-mb--small">{name}</h4>
    )}
    {title && <h5 className="u-ma--remove">{title}</h5>}
  </div>
);

CaseStudyQuoteSection.propTypes = {
  text: PropTypes.string.isRequired,
  name: PropTypes.string,
  title: PropTypes.string,
};

const CaseStudyLayout = ({
  data: {
    pageData: {
      frontmatter: {
        title,
        seoTitle,
        description,
        hero,
        intro,
        quote,
        belowIntro,
        challenges,
        solutions,
        results,
      },
    },
  },
  pageContext: { nodeType },
}) => (
  <BaseLayout>
    <Helmet
      title={seoTitle}
      meta={[
        { name: 'description', content: description },
        { name: 'node-type', content: nodeType },
      ]}
    />
    <Hero
      particles={false}
      className="caseStudy__hero"
      style={{ backgroundImage: `url(${hero.background})` }}
    >
      <div className="hero-content">
        <div className="container">
          <div className="row row--largeGutter row--deepMatch">
            <div className="column-7@medium">
              <div className="caseStudy__hero-left">
                <img
                  src={hero.logo}
                  className="caseStudy__hero-logo"
                  alt={title}
                />
                <h1 className="hero-title u-mt--remove">
                  <Data>{hero.heading}</Data>
                </h1>
                <div className="hero-titleSub">
                  <Data markdown>{hero.blurb}</Data>
                </div>
              </div>
            </div>
            <div className="column-5@medium">
              <div className="caseStudy__hero-right">
                <List type={['space']}>
                  {results.map(result => (
                    <ListItem key={result.heading}>
                      <div className="u-textHeading u-textCenter u-textLeft@medium">
                        <div className="u-textFluid--h3-h4">
                          <Data>{result.heading}</Data>
                        </div>
                        <div className="u-textNormal u-textFluid--xlarge">
                          <Data markdown>{result.blurb}</Data>
                        </div>
                      </div>
                    </ListItem>
                  ))}
                </List>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Hero>
    <div className="container">
      <section className="section">
        <Data markdown>{intro}</Data>
        <CaseStudyQuote
          className="is-single"
          text={quote.text}
          name={quote.name}
          title={quote.title}
          logo={hero.logo}
        />
        {belowIntro && <Data markdown>{belowIntro}</Data>}
      </section>
      {challenges.map(challenge => (
        <section key={challenge.heading} className="section u-borderTop">
          <div className="section-titleWrapper">
            <h5 className="u-textMuted u-textLarge u-mb--remove u-textUppercase">
              Challenge
            </h5>
            {challenge.heading && (
              <h3 className="section-title u-mt--regular">
                <Data>{challenge.heading}</Data>
              </h3>
            )}
          </div>
          <div className="row row--largeGutter">
            <div className="column-6@medium">
              <Data markdown>{challenge.blurb}</Data>
            </div>
            <div className="column-6@medium">
              <CaseStudyQuoteSection
                text={challenge.quote.text}
                name={challenge.quote.name}
                title={challenge.quote.title}
              />
            </div>
          </div>
        </section>
      ))}
      {solutions.map(solution => (
        <section key={solution.heading} className="section u-borderTop">
          <div className="section-titleWrapper">
            <h5 className="u-textMuted u-textLarge u-mb--remove u-textUppercase">
              {solutions.length > 1 ? 'Solution' : 'The Solution'}
            </h5>
            {solution.heading && (
              <h3 className="section-title u-mt--regular">
                <Data>{solution.heading}</Data>
              </h3>
            )}
          </div>
          <div className="row row--largeGutter">
            <div className="column-6@medium">
              <Data markdown>{solution.blurb}</Data>
            </div>
            <div className="column-6@medium">
              <CaseStudyQuoteSection
                text={solution.quote.text}
                name={solution.quote.name}
                title={solution.quote.title}
              />
            </div>
          </div>
        </section>
      ))}
      <section className="section u-borderTop">
        <div className="section-titleWrapper">
          <h3 className="section-title u-mt--regular">Results</h3>
        </div>
        {results.length > 0 && (
          <List type={['space']}>
            {results.map(result => (
              <ListItem key={result.heading}>
                <div className="row row--largeGutter u-flexMiddle">
                  <div className="column-5@medium">
                    <div className="u-textHeading u-textCenter u-textLeft@medium">
                      <div className="u-textFluid--h3-h4">
                        <Data>{result.heading}</Data>
                      </div>
                      <div className="u-textNormal u-textFluid--xlarge">
                        <Data markdown>{result.blurb}</Data>
                      </div>
                    </div>
                  </div>
                  <div className="column-7@medium">
                    <CaseStudyQuoteSection
                      text={result.quote.text}
                      name={result.quote.name}
                      title={result.quote.title}
                    />
                  </div>
                </div>
              </ListItem>
            ))}
          </List>
        )}
      </section>
    </div>
  </BaseLayout>
);

CaseStudyLayout.propTypes = {
  data: PropTypes.object.isRequired,
  pageContext: PropTypes.object,
};

export default CaseStudyLayout;

export const pageQuery = graphql`
  query CaseStudySingle($slug: String!) {
    pageData: markdownRemark(
      fields: { slug: { eq: $slug } }
      fileAbsolutePath: { regex: "/src/case-studies/" }
    ) {
      fileAbsolutePath
      frontmatter {
        title
        seoTitle
        description
        slug
        hero {
          background
          logo
          heading
          blurb
        }
        intro
        quote {
          text
          name
          title
        }
        belowIntro
        challenges {
          heading
          blurb
          quote {
            text
            name
            title
          }
        }
        solutions {
          heading
          blurb
          quote {
            text
            name
            title
          }
        }
        results {
          heading
          blurb
          quote {
            text
            name
            title
          }
        }
      }
      fields {
        slug
      }
    }
  }
`;
